import {
  useState,
  createElement,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
// components
import TreeWidgetMemberDrive from "./components/tree-widget-member-drive";
import TreeWidgetPatientCharts from "./components/tree-widget-patient-charts";
// contexts
import TreeWidgetContextProvider from "./contexts/TreeWidgetContext";
// types
import type { AttachmentsViewProps, SelectedItem } from "../..";
import type { OrchidDriveDirectory } from "contexts/orchid-drive/types";
// hooks
import useToggleModal from "hooks/useToggleModal";

export type TreeWidgetDataContainerProps = {
  folderPaths: OrchidDriveDirectory[];
  children: ReactNode;
  patient?: PatientChartContact;
};

/**
 * renders tree (data structure / move to) widget and provides api to control widget
 */
const TreeWidget = ({
  storageDrive,
  children,
  /**
   * folder paths inside the main view (list / grid view)
   */
  folderPaths: initialFolderPaths,
  patient,
  onMoveEnd,
}: {
  storageDrive: StorageDrive;
  children: ReactNode;
  folderPaths: OrchidDriveDirectory[];
  patient?: PatientChartContact;
  onMoveEnd: AttachmentsViewProps["onMoveEnd"];
}): JSX.Element => {
  const { open, ...showControlProps } = useToggleModal(false);
  const [selected, setSelected] = useState<SelectedItem[]>([]);

  const handleOpen = useCallback(
    (arg: typeof selected) => {
      if (arg) {
        setSelected(arg);
      }
      // reset folder paths to the initial folder paths when open
      setFolderPaths(initialFolderPaths);
      open();
    },
    [initialFolderPaths, open],
  );

  const [folderPaths, setFolderPaths] =
    useState<OrchidDriveDirectory[]>(initialFolderPaths);

  // on main view current folder / select change, need to sync folder path w/ the main view
  useEffect(
    () => setFolderPaths(initialFolderPaths),
    [initialFolderPaths, selected],
  );

  const handleMoveEnd = useCallback(
    (arg) => {
      if (arg.success) {
        setSelected([]);
      }
      return onMoveEnd && onMoveEnd(arg);
    },
    [onMoveEnd],
  );

  return storageDrive === "note-attachment" ? (
    (children as any)
  ) : (
    <TreeWidgetContextProvider
      onMoveEnd={handleMoveEnd}
      selected={selected}
      originFolder={initialFolderPaths[initialFolderPaths.length - 1]}
      folderPaths={folderPaths}
      setFolderPaths={setFolderPaths}
      open={handleOpen}
      {...showControlProps}
    >
      {createElement(
        storageDrive === "member-drive"
          ? TreeWidgetMemberDrive
          : storageDrive === "patient-chart"
          ? TreeWidgetPatientCharts
          : "div",
        {
          patient,
          folderPaths,
          children,
        },
      )}
    </TreeWidgetContextProvider>
  );
};

export default TreeWidget;
